import { useEffect, useState } from "react";
import {
  RequirementConfirmShowEnum,
  RequirementConfirmTypeEnum,
  RequirementConfirmValueEnum
} from "@/types/requirement";
import { ListPathProps } from "@/apis/requirement";
// flushSync removed: avoid calling it inside render lifecycle to prevent React warnings
import usePage from "@/layout/hooks/usePage";
import { PageId } from "@/types/page";

const useRequirementParams = (isGetInfo?: boolean) => {
  const { pageId, pageType } = usePage();

  const [params, setParams] = useState<{
    params: Array<ListPathProps>;
    confirmType: RequirementConfirmTypeEnum | undefined;
    confirmValue: RequirementConfirmValueEnum | undefined;
  }>({
    params: [],
    confirmType: undefined,
    confirmValue: undefined
  });

  useEffect(() => {
    if (pageId && isGetInfo) {
      getInfo(pageId);
    }
  }, [pageId]);

  const getInfo = (type: PageId) => {
    const typeObj = {
      [PageId.MC_REQ_MANAGE]: RequirementConfirmTypeEnum.MATERIAL_CONTROL,
      [PageId.MC_REQ_M]: RequirementConfirmTypeEnum.MATERIAL_CONTROL,
      [PageId.MC_PASSED]: RequirementConfirmTypeEnum.MATERIAL_CONTROL,
      [PageId.MC_BE_REJECT]: RequirementConfirmTypeEnum.MATERIAL_CONTROL,
      [PageId.MC_OFFICIAL_PENDING]: RequirementConfirmTypeEnum.MATERIAL_CONTROL,
      [PageId.MC_OFFICIAL_DONE]: RequirementConfirmTypeEnum.MATERIAL_CONTROL,
      [PageId.MC_OFFICIAL_CLOSED]: RequirementConfirmTypeEnum.MATERIAL_CONTROL,
      [PageId.PUR_NOT_PASS]: RequirementConfirmTypeEnum.PURCHASE,
      [PageId.PUR_PASSED]: RequirementConfirmTypeEnum.PURCHASE,
      [PageId.PUR_BE_REJECT]: RequirementConfirmTypeEnum.PURCHASE,
      [PageId.PUR_OFFICIAL_PENDING]: RequirementConfirmTypeEnum.PURCHASE,
      [PageId.PUR_OFFICIAL_DONE]: RequirementConfirmTypeEnum.PURCHASE,
      [PageId.PUR_OFFICIAL_CLOSED]: RequirementConfirmTypeEnum.PURCHASE,
      [PageId.PUR_ALL_PASSED]: RequirementConfirmTypeEnum.PURCHASE,

      [PageId.COMMODITY_NOT_PASS]: RequirementConfirmTypeEnum.COMMODITY,
      [PageId.COMMODITY_PASSED]: RequirementConfirmTypeEnum.COMMODITY,
      [PageId.COMMODITY_BE_REJECT]: RequirementConfirmTypeEnum.COMMODITY,
      [PageId.COMMODITY_OFFICIAL_PENDING]: RequirementConfirmTypeEnum.COMMODITY,
      [PageId.COMMODITY_OFFICIAL_DONE]: RequirementConfirmTypeEnum.COMMODITY,
      [PageId.COMMODITY_OFFICIAL_CLOSED]: RequirementConfirmTypeEnum.COMMODITY,

      [PageId.PROD_NOT_PASS]: RequirementConfirmTypeEnum.PROD,
      [PageId.PROD_PASSED]: RequirementConfirmTypeEnum.PROD,
      [PageId.PROD_BE_REJECT]: RequirementConfirmTypeEnum.PROD,
      [PageId.PROD_OFFICIAL_PENDING]: RequirementConfirmTypeEnum.PROD,
      [PageId.PROD_OFFICIAL_DONE]: RequirementConfirmTypeEnum.PROD,
      [PageId.PROD_OFFICIAL_CLOSED]: RequirementConfirmTypeEnum.PROD,
      [PageId.F_PASSED]: RequirementConfirmTypeEnum.FINANCE,
      [PageId.F_NOT_PASS]: RequirementConfirmTypeEnum.FINANCE,
      [PageId.F_BE_REJECT]: RequirementConfirmTypeEnum.FINANCE,
      [PageId.SALE_OFFICIAL]: RequirementConfirmTypeEnum.SALE,
      [PageId.F_SALE_OFFICIAL]: RequirementConfirmTypeEnum.SALE,
      [PageId.SALE_NOT_PASS]: RequirementConfirmTypeEnum.SALE
    } as Partial<Record<PageId, RequirementConfirmTypeEnum>>;

    const valueObj = {
      [PageId.MC_REQ_MANAGE]: RequirementConfirmValueEnum.PASSED,
      [PageId.MC_REQ_M]: RequirementConfirmValueEnum.PASSED,
      [PageId.MC_PASSED]: RequirementConfirmValueEnum.PASSED,
      [PageId.MC_BE_REJECT]: RequirementConfirmValueEnum.REJECTED,
      [PageId.MC_OFFICIAL_PENDING]: RequirementConfirmValueEnum.PASSED,
      [PageId.MC_OFFICIAL_DONE]: RequirementConfirmValueEnum.PASSED,
      [PageId.MC_OFFICIAL_CLOSED]: RequirementConfirmValueEnum.PASSED,
      [PageId.PUR_NOT_PASS]: RequirementConfirmValueEnum.NOT_PASS,
      [PageId.PUR_PASSED]: RequirementConfirmValueEnum.PASSED,
      [PageId.PUR_BE_REJECT]: RequirementConfirmValueEnum.REJECTED,
      [PageId.PUR_OFFICIAL_PENDING]: RequirementConfirmValueEnum.PASSED,
      [PageId.PUR_OFFICIAL_DONE]: RequirementConfirmValueEnum.PASSED,
      [PageId.PUR_OFFICIAL_CLOSED]: RequirementConfirmValueEnum.PASSED,

      [PageId.COMMODITY_NOT_PASS]: RequirementConfirmValueEnum.NOT_PASS,
      [PageId.COMMODITY_PASSED]: RequirementConfirmValueEnum.PASSED,
      [PageId.COMMODITY_BE_REJECT]: RequirementConfirmValueEnum.REJECTED,
      [PageId.COMMODITY_OFFICIAL_PENDING]: RequirementConfirmValueEnum.PASSED,
      [PageId.COMMODITY_OFFICIAL_DONE]: RequirementConfirmValueEnum.PASSED,
      [PageId.COMMODITY_OFFICIAL_CLOSED]: RequirementConfirmValueEnum.PASSED,

      [PageId.PROD_NOT_PASS]: RequirementConfirmValueEnum.NOT_PASS,
      [PageId.PROD_PASSED]: RequirementConfirmValueEnum.PASSED,
      [PageId.PROD_BE_REJECT]: RequirementConfirmValueEnum.REJECTED,
      [PageId.PROD_OFFICIAL_PENDING]: RequirementConfirmValueEnum.PASSED,
      [PageId.PROD_OFFICIAL_DONE]: RequirementConfirmValueEnum.PASSED,
      [PageId.PROD_OFFICIAL_CLOSED]: RequirementConfirmValueEnum.PASSED,

      [PageId.F_PASSED]: RequirementConfirmValueEnum.PASSED,
      [PageId.F_NOT_PASS]: RequirementConfirmValueEnum.NOT_PASS,
      [PageId.F_BE_REJECT]: RequirementConfirmValueEnum.REJECTED,
      [PageId.SALE_OFFICIAL]: RequirementConfirmValueEnum.PASSED,
      [PageId.F_SALE_OFFICIAL]: RequirementConfirmValueEnum.PASSED,
      [PageId.SALE_NOT_PASS]: RequirementConfirmValueEnum.NOT_PASS,
      [PageId.PUR_ALL_PASSED]: RequirementConfirmValueEnum.PASSED
    } as Partial<Record<PageId, RequirementConfirmValueEnum>>;

    const allConfirms = [
      PageId.MC_OFFICIAL_CLOSED,
      PageId.MC_OFFICIAL_DONE,
      PageId.MC_OFFICIAL_PENDING,

      PageId.COMMODITY_OFFICIAL_CLOSED,
      PageId.COMMODITY_OFFICIAL_DONE,
      PageId.COMMODITY_OFFICIAL_PENDING,

      PageId.PROD_OFFICIAL_CLOSED,
      PageId.PROD_OFFICIAL_DONE,
      PageId.PROD_OFFICIAL_PENDING,
      PageId.PUR_OFFICIAL_CLOSED,
      PageId.PUR_OFFICIAL_DONE,
      PageId.PUR_OFFICIAL_PENDING,
      PageId.PUR_ALL_PASSED
    ];
    if (pageId && allConfirms.includes(pageId)) {
      setParams({
        params: [
          {
            type: RequirementConfirmTypeEnum.MATERIAL_CONTROL,
            value: RequirementConfirmValueEnum.PASSED
          },
          {
            type: RequirementConfirmTypeEnum.COMMODITY,
            value: RequirementConfirmValueEnum.PASSED
          },
          {
            type: RequirementConfirmTypeEnum.PROD,
            value: RequirementConfirmValueEnum.PASSED
          },
          {
            type: RequirementConfirmTypeEnum.PURCHASE,
            value: RequirementConfirmValueEnum.PASSED
          },
          {
            type: RequirementConfirmTypeEnum.FINANCE,
            value: RequirementConfirmValueEnum.PASSED
          },
          {
            type: RequirementConfirmTypeEnum.SALE,
            value: RequirementConfirmValueEnum.PASSED
          }
        ],
        confirmType: typeObj[type] ?? undefined,
        confirmValue: valueObj[type] ?? undefined
      });
    } else {
      setParams({
        params: [
          {
            type: typeObj[type] as RequirementConfirmTypeEnum,
            value: valueObj[type] as RequirementConfirmValueEnum,
            show: RequirementConfirmShowEnum.SHOW
          }
        ],
        confirmType: typeObj[type] ?? undefined,
        confirmValue: valueObj[type] ?? undefined
      });
    }
  };

  return { params, pageId, pageType };
};

export default useRequirementParams;
